import { Injectable } from "@angular/core";
import { DatalayerService } from "@edp-services/datalayer.service";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import datalayerConfigs from "../../../assets/jsons/datalayerConfigs.json";
import { EdpModalAlertComponent } from "./alert/edp-modal-alert.component";
import { EdpModalConfirmComponent } from "./confirm/edp-modal-confirm.component";
import { EdpModalComponent } from "./edp-modal.component";
import { IModal, IModalData } from "./edp-modal.models";

@Injectable({
    providedIn: "root",
})
export class EdpModalService {
    private readonly BACKDROP_CLASS: string = "edp-modal-backdrop";
    private readonly WINDOW_CLASS: string = "edp-modal-window";
    private readonly OPEN_CLASS: string = "opened";
    private readonly CLOSE_CLASS: string = "hidden";
    private readonly CLOSE_TIMING: number = 100;
    private modals: IModal[] = [];
    private options: NgbModalOptions;
    private datalayer: string;
    private extraDatalayer: any;

    constructor(private ngbModalService: NgbModal, private datalayerService: DatalayerService) {
        this.options = {
            centered: true,
            windowClass: this.WINDOW_CLASS,
            backdropClass: this.BACKDROP_CLASS,
        };
    }

    private open(component: any, options: NgbModalOptions, data?: IModalData): Promise<any> {
        data.service = this;
        let modal: IModal = this.modals.find((modalInstance) => modalInstance.component instanceof component);

        if (!modal) {
            modal = {
                instance: this.ngbModalService.open(EdpModalComponent, options),
                data,
                windowClass: options.windowClass ? options.windowClass : this.options.windowClass,
                backdropClass: options.backdropClass ? options.backdropClass : this.options.backdropClass,
                component,
            };

            this.modals.push(modal);

            modal.instance.componentInstance.data = data;
            modal.instance.componentInstance.injectComponent(component);
        }

        return modal.instance.result;
    }

    private remove(method: string, result?: any): void {
        const modal: IModal = this.modals.pop();
        const container = Array.from(document.getElementsByClassName(modal.windowClass)).pop();
        const backdrop = Array.from(document.getElementsByClassName(modal.backdropClass)).pop();
        try {
            container.querySelector(`.${this.OPEN_CLASS}`).classList.remove(this.OPEN_CLASS);
            backdrop.classList.add(this.CLOSE_CLASS);
            setTimeout(() => modal.instance[method](result), this.CLOSE_TIMING);
        } catch (error) {
            console.warn("error on close modal:", error);
        }
    }

    public removeClass(className: string): void {
        const modal: IModal = this.modals[this.modals.length - 1];
        if (!modal) {
            return;
        }
        try {
            document.getElementsByClassName(modal.windowClass)[0].classList.remove(className);
            modal.windowClass = modal.windowClass.replace(className, "");
        } catch (error) { }
    }

    public close(result?: any): void {
        this.remove("close", result);
    }

    public dismiss(result?: any, event?: string): void {
        this.setDatalayerClose(event);
        this.remove("dismiss", result);
    }

    private setEventDataLayer(data: any): void {
        if (!data?.collection) {
            return;
        }

        if (!!data.collection.gaClick) {
            this.datalayer = data.collection.gaClick;
            this.extraDatalayer = !!data.collection.extraGaClick ? data.collection.extraGaClick : null;
            this.datalayerService.setGaClick(data.collection.gaClick, this.extraDatalayer);
        }

        if (!!data.collection.gaEvent) {
            this.datalayerService.setGaEvent(data.collection.gaEvent, !!data.collection.extraGaEvent ? data.collection.extraGaEvent : null);

            if (!this.datalayer) {
                this.extraDatalayer = !!data.collection.extraGaEvent ? data.collection.extraGaEvent : null;
                this.datalayer = !data.collection.extraGaEvent
                    ? data.collection.gaEvent
                    : { ...data.collection.gaEvent, ...data.collection.extraGaEvent };
            }
        }
    }

    public custom(component: any, options?: NgbModalOptions, data?: any): Promise<any> {
        const finalOptions = Object.assign({}, this.options, options);
        this.setEventDataLayer(data);

        return this.open(component, finalOptions, data);
    }

    public confirm(data: IModalData, options?: NgbModalOptions): Promise<any> {
        const finalOptions = Object.assign({}, this.options, options);

        return this.open(EdpModalConfirmComponent, finalOptions, data);
    }

    public alert(data: IModalData, options?: NgbModalOptions): Promise<any> {
        const finalOptions = Object.assign({}, this.options, {
            keyboard: false,
            backdrop: "static"
        }, options);

        return this.open(EdpModalAlertComponent, finalOptions, data);
    }

    public hasInstance() {
        return this.modals;
    }

    public removeAll() {
        this.ngbModalService.dismissAll();
    }

    private setDatalayerClose(eventName?: string) {
        if (eventName) {
            this.datalayerService.setGaClick("clickClose", {
                interactionName: eventName,
                interactionLocation: "ModalAlteracaoUtilizacaoCasa",
            });
        }
        if (!!this.datalayer && !eventName) {
            this.datalayerService.setGaClick("clickClose", {
                interactionLocation: !!this.extraDatalayer ? this.extraDatalayer.formModal : datalayerConfigs[this.datalayer].formModal,
            });
            this.datalayer = null;
        }
    }
}
